import {createSelector} from 'reselect';

import {AppState} from '../../../../../verticals/hotels-dweb/src/shared/store';
import {
  HostInfoShape,
  ExactRoom,
  SharedSpaces,
  SoldOutOrderKeys,
  TemplateDataSearchRoom,
  DetailsUnifShape,
  ValidSearchRoom,
  HotelPersuasions,
  LOVEDBYGUEST,
  HotelDetailsFromSearch,
  ContextDetails,
  RoomBase,
  LocationDetail,
  AllInclusiveCard,
  AllInclusiveInclusion,
  FeatureFlags,
  GroupFilter,
  OccupancyRoom,
  GoTribeInfo,
  LuckyData,
  CardFilter,
} from '@stencil/hotels-types/src/details-cg/types';
import {ReviewSummaryGI} from '@stencil/hotels-types/src/common/types/review-gi';

import {NON_OPERATIONAL_ERROR} from '@stencil/hotels-config';
import {isValidArray} from '@stencil/hotels-utils/src/common-utils';

export const detailsUnifSelector = (state: AppState) => state.detailsUnif as DetailsUnifShape;

/* ------- Common Selectors ------- */
export const srpFilterSelector = createSelector(
  detailsUnifSelector,
  details => details?.selectedHotelStatic?.srpContinuedFilters || {},
);
export const selectedHotelStatic = createSelector(
  detailsUnifSelector,
  details => details?.selectedHotelStatic || {},
);

export const isDetailsSeoSelector = createSelector(detailsUnifSelector, details => details?.isSeo);
export const isAltAccoUrlPropsSelector = createSelector(
  detailsUnifSelector,
  details => details?.isAltAccoUrlProps,
);

export const offerCodeSelector = createSelector(detailsUnifSelector, details => {
  return details?.offerCodeFromQueryParam;
});

export const selectedRatePlanSelector = createSelector(detailsUnifSelector, details => {
  return details?.selectedRatePlan || {};
});

export const detailsUnifSeoReviewsSelector = createSelector(
  detailsUnifSelector,
  details => details?.seoReviewsData,
);

export const currentRoomData = createSelector(
  detailsUnifSelector,
  details => details?.UI?.currentRoomData,
);

/* ------- Static Data Selectors ------- */
export const staticDataSelector = createSelector(detailsUnifSelector, details => details?.static);

export const staticApiRequestSelector = createSelector(
  detailsUnifSelector,
  details => details?.staticDetailsRequestState,
);

export const hotelDetailsSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.hotelDetails,
);

export const showVideoDwebSelector = createSelector(staticDataSelector, hotelDetails => {
  return !hotelDetails?.altAcco;
});

export const uuidSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.uuids || [],
);

export const soldOutWidgetOrderSelector = createSelector(
  staticDataSelector,
  staticData =>
    staticData?.widget_display_order || [
      SoldOutOrderKeys.AlternateDates,
      SoldOutOrderKeys.SimilarProperties,
    ],
);

export const imageGallerySelector = createSelector(
  staticDataSelector,
  staticData => staticData?.media?.imageGallery,
);

export const poiDataSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.placesResponse?.categories,
);

export const coupleFriendlySelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.coupleFriendlyPersuasion || null,
);

export const fndDataSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.foodAndDining,
);

export const foodDiningAltAccoDataSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.foodDining || {},
);

export const nidhiBannerDataSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.govtPolicies || [],
);

export const hotelNameSelector = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.name,
);

export const hotelLocationSelector = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.locationDetail || ({} as LocationDetail),
);
export const cityIdSelector = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.locationDetail?.id,
);

export const hotelStaticHouseRulesSelector = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.houseRulesV2?.allRules,
);

export const checkInTimeFromMetaInfo = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.checkinTime,
);

export const checkOutTimeFromMetaInfo = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.checkoutTime,
);

export const checkInTimeV2Selector = createSelector(
  hotelDetailsSelector,
  checkInTimeFromMetaInfo,
  (hotelDetailsData, checkInTime) => hotelDetailsData?.checkinTimeRange || checkInTime,
);

export const checkOutTimeV2Selector = createSelector(
  hotelDetailsSelector,
  checkOutTimeFromMetaInfo,
  (hotelDetailsData, checkOutTime) => hotelDetailsData?.checkoutTimeRange || checkOutTime,
);

export const isAltAccoSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.altAcco,
);

export const isGoStaysSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.goStay,
);

export const locationDetailsSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.locationDetail || {},
);

export const sharingUrlSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.sharingUrl || {},
);
export const hotelMMTIdSelector = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.id,
);

export const hostInfoSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.hostInfoV2 as HostInfoShape,
);

export const similarPropertiesSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.hotelCompareResponse?.hotelList || [],
);

export const similarPropertiesRankingAlgoSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.hotelCompareResponse?.rankingAlgo,
);

export const reviewSummaryGISelector = createSelector(staticDataSelector, staticData => {
  return staticData?.reviewSummaryGI || ({} as ReviewSummaryGI);
});

export const roomInfoMapSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.roomInfoMap,
);

export const reviewRatingSelector = createSelector(staticDataSelector, staticData => {
  return staticData?.reviewSummaryGI;
});

export const tasteRatingSelector = createSelector(staticDataSelector, staticData => {
  return staticData?.reviewSummaryGI?.giData?.ratingsMap?.tasteRating?.rating;
});

export const ambienceRatingSelector = createSelector(staticDataSelector, staticData => {
  return staticData?.reviewSummaryGI?.giData?.ratingsMap?.ambienceRating?.rating;
});

export const mediaV2Selector = createSelector(staticDataSelector, staticData => {
  return staticData?.mediaV2;
});

export const ugcSummaryGISelector = createSelector(
  staticDataSelector,
  staticData => staticData?.ugcSummary,
);

export const ratingSummaryGISelector = createSelector(
  staticDataSelector,
  staticData => staticData?.ugcSummary?.ratingSummaryGI,
);

const getUgcV2RatingData = (ratingSummary, category) => {
  const categoryData = ratingSummary?.find(item => item.concept === category);
  const rating = categoryData?.value ? ((categoryData.value / 100) * 5).toFixed(1) : null;
  return {...categoryData, overall_rating: rating};
};

const getRatingData = (reviewRating, category) =>
  reviewRating?.giData?.effectiveCategoryData?.find(item => item.id === category);

const getCategoryRating = (commonData, reviewRating, ratingSummary, category) => {
  return getUgcV2RatingData(ratingSummary, category);
};

export const amenityRatingSelector = createSelector(
  [state => state.common, reviewRatingSelector, ratingSummaryGISelector],
  (commonData, reviewRating, ratingSummary) =>
    getCategoryRating(commonData, reviewRating, ratingSummary, 'Amenities'),
);

export const locationRatingSelector = createSelector(
  [state => state.common, reviewRatingSelector, ratingSummaryGISelector],
  (commonData, reviewRating, ratingSummary) =>
    getCategoryRating(commonData, reviewRating, ratingSummary, 'Location'),
);

export const foodRatingSelector = createSelector(
  [state => state.common, reviewRatingSelector, ratingSummaryGISelector],
  (commonData, reviewRating, ratingSummary) => {
    const foodCategoryRating = getCategoryRating(commonData, reviewRating, ratingSummary, 'Food');

    const foodAmbienceRating = reviewRating?.giData?.ratings?.find(
      category => category.id === 'ambienceRating',
    );

    const tasteRating = reviewRating?.giData?.ratings?.find(
      category => category.id === 'tasteRating',
    );

    return {foodCategoryRating, foodAmbienceRating, tasteRating};
  },
);

export const isEntirePropertySelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.entireProperty,
);

export const stayTypeSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.stayType,
);

export const propertyLabelSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.propertyLabel,
);

export const captivateGalleryDwebSelector = createSelector(imageGallerySelector, imageGallery => {
  return imageGallery?.galleryExpThree?.filter(g => g?.type?.toLowerCase() !== 'video') || [];
});

export const galleryPopupTypeSelector = createSelector(
  detailsUnifSelector,
  details => details?.activeGalleryPopupType || 'hotellier',
);

export const highlightedAmenitiesSelector = createSelector(
  hotelDetailsSelector,
  hotelDetailsData => hotelDetailsData?.highlightedAmenities,
);

export const amenitiesSelector = createSelector(
  staticDataSelector,
  staticData =>
    staticData?.amenitiesGI?.categorized ||
    staticData?.hotelDetails?.amenitiesGI?.categorized ||
    [],
);

export const streaksBannerSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.userStreaksInfoResponse,
);

export const indiannessSelector = createSelector(
  staticDataSelector,
  staticData => staticData?.detailPersuasionCards?.cardData?.LOVEDBYGUEST || ({} as LOVEDBYGUEST),
);

export const allVideosSelector = createSelector(
  hotelDetailsSelector,
  hotelDetails => hotelDetails?.hotelVideos,
);

export const videoMediaSelector = createSelector(allVideosSelector, item => item?.videoMedia || []);

export const videoMediaExistsSelector = createSelector(videoMediaSelector, item => !!item?.length);

export const videoSnapshotUrlsSelector = createSelector(
  videoMediaExistsSelector,
  videoMediaSelector,
  (videoMediaExists, videoMedia) => {
    if (!videoMediaExists) return [];

    return videoMedia.map(item => {
      const snapshotUrl = item?.processedVideos?.snapshotUrl;
      return {
        b: snapshotUrl,
        m: snapshotUrl,
        type: item?.type,
        id: item.id,
      };
    });
  },
);

export const expediaReviewInfoSelector = createSelector(reviewSummaryGISelector, reviewRating => {
  const obj = {
    expediaReviewExists: reviewRating?.expDataExists && reviewRating?.expData?.reviewCount > 0,
    // onlyExpediaExists: !reviewRating?.giDataExists && reviewRating?.expDataExists,
    onlyExpediaExists:
      reviewRating?.ugcFunnelReviewSrc?.toLowerCase() === 'exp' ||
      (!reviewRating?.giDataExists && reviewRating?.expDataExists),
    expData: reviewRating?.expData,
  };
  return obj;
});

/* ------- Search Room Selectors ------- */
export const searchRoomApiRequestSelector = createSelector(
  detailsUnifSelector,
  details => details?.searchRoomRequestState,
);

export const propertyUnavailableSelector = createSelector(
  detailsUnifSelector,
  details => details?.searchRoomRequestState?.error?.code === NON_OPERATIONAL_ERROR,
);

export const searchRoomDataSelector = createSelector(
  detailsUnifSelector,
  details => details?.searchRooms?.data || ({} as ValidSearchRoom),
);

export const luckyDataSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.luckyData || ({} as LuckyData),
);

export const templateDataSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => (searchRoom?.templateData || {}) as TemplateDataSearchRoom,
);

export const appliedFiltersSelector = createSelector(
  detailsUnifSelector,
  item => item.appliedFiltersInUrl || ([] as string[]),
);

export const hotelPersuasionsDetailsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.hotelPersuasions || ({} as HotelPersuasions),
);

export const hotelCardDataDetailsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.cardData || [],
);

export const goTribePersuasionsSelector = createSelector(
  hotelPersuasionsDetailsSelector,
  hotelPersuasions => {
    const goTribePersuasion = hotelPersuasions?.goTribeBenefits;
    if (!goTribePersuasion) return null;
    const textsList = goTribePersuasion?.map(persuasion => persuasion?.persuasionText);
    const fisrtPersuasion = goTribePersuasion?.[0];
    const goTribeDetails = {
      textsList,
      imageUrl: fisrtPersuasion?.imagesList?.[0] || '',
      textColor: fisrtPersuasion?.colourCodeData?.textColour || '',
      lineColor: fisrtPersuasion?.colourCodeData?.borderColour || '',
    };
    return goTribeDetails;
  },
);

export const hotelDetailsSoldOutStatus = createSelector(
  detailsUnifSelector,
  details => !!details?.searchRooms?.data?.error,
);

export const selectedRoomSelector = createSelector(
  detailsUnifSelector,
  details => details?.selectedRoom || {},
);

export const hotelDiscountPersuationsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.hotelPersuasions?.discount?.[0],
);

export const hotelDealsPersuationsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.hotelPersuasions?.deals || [],
);

export const appliedGocashTextSelector = createSelector(searchRoomDataSelector, searchRoom => '');

export const noOfOffersTextSelector = createSelector(searchRoomDataSelector, searchRoom => '');

export const packagesSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.packageRooms || [],
);

export const recommendedCombosSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.recommendedCombos || [],
);

export const occupancyRoomsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.occupancyRooms,
);

export const roomFiltersDropdownSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.groupFilters || ([] as GroupFilter[]),
);

export const cardFiltersSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.cardFilter?.cardFilters || ([] as CardFilter[]),
);

export const roomInfoSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.roomInfo || {},
);

export const firstRoomRatePlanDataSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.exactRooms?.[0]?.ratePlans?.[0],
);

export const altAccoPropertyTypeSelector = createSelector(
  isEntirePropertySelector,
  firstRoomRatePlanDataSelector,
  propertyLabelSelector,
  (isEntireProperty, ratePlan, propertyTypeLabel) => {
    let altAccoProperty = 'Hotel';
    if (isEntireProperty) {
      if (propertyTypeLabel) {
        altAccoProperty = propertyTypeLabel;
      } else if (ratePlan?.sellableType) {
        altAccoProperty = ratePlan.sellableType;
      }
    }
    return altAccoProperty;
  },
);

export const altAccoPropertyLabelSelector = createSelector(
  isAltAccoSelector,
  propertyLabelSelector,
  (isAltAcco, propertyTypeLabel) => {
    let altAccoLabel = 'Hotel';
    if (isAltAcco) {
      if (propertyTypeLabel) {
        altAccoLabel = propertyTypeLabel;
      }
    }
    return altAccoLabel;
  },
);

export const tariffPlanDataSelector = createSelector(firstRoomRatePlanDataSelector, ratePlan => {
  const tariffPersuasion = ratePlan?.tariffPersuasion;

  return [
    ...(tariffPersuasion?.goTribeOffer || []),
    ...(tariffPersuasion?.specialOffer || []),
    ...(tariffPersuasion?.loggedInOffer || []),
    ...(tariffPersuasion?.mobileOffer || []),
  ];
});

export const exactRoomsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => (searchRoom?.exactRooms || []) as ExactRoom[],
);

export const sharedSpacesSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => (searchRoom?.sharedSpaces || {}) as SharedSpaces,
);

export const propertyLayoutTitleSelector = createSelector(
  [hotelDetailsSelector, searchRoomDataSelector],
  (hotelDetails, searchRoom) =>
    searchRoom?.propertyLayoutTitleText || hotelDetails?.propertyLayoutTitleText || '',
);

export const multiRoomFlowStatusSelector = createSelector(
  searchRoomDataSelector,
  searchRoomData => {
    const hasRecommendedCombos = isValidArray(searchRoomData?.recommendedCombos);
    const hasOccupancyRooms = isValidArray(searchRoomData?.occupancyRooms);

    return {
      isMultiRoomFlow: hasRecommendedCombos || hasOccupancyRooms,
      isOnlyRecommendedCombos: hasRecommendedCombos && !hasOccupancyRooms,
      isOnlyOccupancyRooms: !hasRecommendedCombos && hasOccupancyRooms,
      isComboWithRoom: hasRecommendedCombos && hasOccupancyRooms,
      hasRecommendedCombos,
      hasOccupancyRooms,
    };
  },
);

// Improved roomsDataSelector with type safety
export const roomsDataSelector = createSelector(
  [searchRoomDataSelector, multiRoomFlowStatusSelector],
  (searchRoomData, multiRoomFlowStatus): ExactRoom[] | OccupancyRoom[] => {
    if (multiRoomFlowStatus?.hasOccupancyRooms) {
      return searchRoomData?.occupancyRooms || [];
    } else {
      return searchRoomData?.exactRooms || [];
    }
  },
);

export const singleRoomSelector = createSelector(roomsDataSelector, roomData => {
  return roomData?.length === 1;
});

export const hotelDetailsSoldOut = createSelector(
  [searchRoomApiRequestSelector, roomsDataSelector, multiRoomFlowStatusSelector],
  (searchRoomApiRequest, roomsData, multiRoomFlowStatus) => {
    const isSoldOut =
      searchRoomApiRequest?.success &&
      !isValidArray(roomsData as any[]) &&
      !multiRoomFlowStatus?.isOnlyRecommendedCombos;
    return isSoldOut;
  },
);

export const exactRoomsDataSelector = createSelector(searchRoomDataSelector, searchRoomData => {
  return searchRoomData?.exactRooms || ([] as ExactRoom[]);
});

export const entirePropertyMergedTypeSelector = createSelector(
  searchRoomDataSelector,
  searchRoomData => {
    return searchRoomData?.hotelDetails?.entireProperty
      ? searchRoomData?.hotelDetails?.mergedPropType || ''
      : '';
  },
);

export const sellableTypeSelector = createSelector(
  roomsDataSelector,
  data => data?.[0]?.ratePlans?.[0]?.sellableType,
);

export const inclusiveRatePlanSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.allInclusiveCard || ({} as AllInclusiveCard),
);

export const inclusiveRatePlanInclusionsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.allInclusiveInclusions || ([] as AllInclusiveInclusion[]),
);

export const cfhFromMetaInfo = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.carryForwardHash || '',
);

export const previousBookingDataSelector = createSelector(
  detailsUnifSelector,
  details => details?.searchRooms?.data?.previousBookingData || ([] as RoomBase[]),
);

export const searchHotelDetailsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.hotelDetails || ({} as HotelDetailsFromSearch),
);

export const contextDetailsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.contextDetails || ({} as ContextDetails),
);

export const goTribeBenefitsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.goTribeInfo || ({} as GoTribeInfo),
);

export const featureFlagsSelector = createSelector(
  searchRoomDataSelector,
  searchRoom => searchRoom?.featureFlags || ({} as FeatureFlags),
);

/* ------- Multi Room Cart Selectors ------- */
export const isCartUpdatingStatusSelector = createSelector(
  detailsUnifSelector,
  detailsData => detailsData?.fetchCartDataStatus,
);

export const cartResponseSelector = createSelector(
  detailsUnifSelector,
  detailsData => detailsData?.cartResponseData || {},
);

export const cartRequestPayloadSelector = createSelector(
  detailsUnifSelector,
  detailsData => detailsData?.cartRequestPayload || {},
);

export const multiRoomCartDataSelector = createSelector(
  detailsUnifSelector,
  detailsData => detailsData.cartRoomUIData || {},
);

export const multiRoomArcInfo = createSelector(
  detailsUnifSelector,
  detailsData => detailsData.arcWithRtc || {},
);

export const appliedFiltersList = createSelector(
  detailsUnifSelector,
  details => details?.appliedFiltersList || [],
);

export const appliedFiltersInUrlSelector = createSelector(
  detailsUnifSelector,
  item => item.appliedFiltersInUrl || [],
);

export const multiRoomCartPaymodeSelector = createSelector(
  detailsUnifSelector,
  detailsData => detailsData.cartPayMode || {},
);

/* -------------------- PWA Amenties Selectors -------------------*/

export const amenitiesSelectorMweb = createSelector(
  [staticDataSelector],
  staticData => staticData?.amenitiesGI,
);

export const popularAmenitiesSelector = createSelector(amenitiesSelectorMweb, amenitiesData => {
  let amenities = amenitiesData?.rated || amenitiesData?.transformed || [];

  if (amenities?.length < 6 && !!amenitiesData?.unrated?.length) {
    amenities = amenities.concat(amenitiesData?.unrated);
  }

  return amenities?.map(({code, name}) => ({
    amenityCode: code,
    amenityName: name,
    imgSrc: amenitiesData?.amenitiesMedia?.[code]?.image || '',
  }));
});

export const amenitiesCountSelector = createSelector(
  amenitiesSelectorMweb,
  amenitiesData => amenitiesData?.transformed?.length || 0,
);

export const categorizedAmenitiesSelector = createSelector(amenitiesSelectorMweb, amenitiesData => {
  return (
    amenitiesData?.categorized?.map(({label, data}) => ({heading: label, amenitiesList: data})) ||
    []
  );
});

/* -------------------- PWA Gallery Selectorss -------------------*/

export const captivateGalleryMwebSelector = createSelector(imageGallerySelector, imageGallery => {
  return imageGallery?.galleryExpThree || [];
});

/* ------- Alternate Date Selectors ------- */

export const alternateDatesSelector = createSelector(
  detailsUnifSelector,
  details => details?.alternateDates?.data || [],
);

export const alternateDatesHotelIdSelector = createSelector(
  detailsUnifSelector,
  details => details?.alternateDates?.hotelId || 0,
);

export const isAlternateDatesDataSelector = createSelector(
  alternateDatesSelector,
  item => item?.length > 2,
);

export const alternateDatesRequestSelector = createSelector(
  detailsUnifSelector,
  details => details?.alternateDatesRequestState,
);

export const detailsHasDataSelector = createSelector(
  hotelDetailsSelector,
  roomsDataSelector,
  (hasStaticData, hasSearchRoomsData) => hasSearchRoomsData || hasStaticData,
);

/* ------- Hotelier Media Selectors ------- */

export const hotelierMediaSelector = createSelector(detailsUnifSelector, details => {
  const defaultTaggedMedia = details?.hotelierMediaData?.taggedMedia || [];
  return details?.hotelierMediaData?.taggedMedia_v3 || defaultTaggedMedia;
});

export const hotelierMediaRequestSelector = createSelector(
  detailsUnifSelector,
  details => details?.hotelierMediaRequestState,
);

export const hotelierMediaByTagRequestSelector = createSelector(
  detailsUnifSelector,
  details => details?.hotelierMediaByTagIdStatus,
);

export const hotelierMediaByTagSelector = createSelector(
  detailsUnifSelector,
  details => details?.hotelierMediaDataByTag,
);

/* ------- Day use Selectors ------- */

export const slotsRequestStateSelector = createSelector(
  detailsUnifSelector,
  details => details?.slotsRequestState,
);

export const slotsDayUseDataSelector = createSelector(
  detailsUnifSelector,
  details => details?.slotsDayUseData?.data,
);

export const hasSlotsDayUseDataSelector = createSelector(
  slotsDayUseDataSelector,
  slotsDayUseData => !!Object.keys(slotsDayUseData)?.length || false,
);

export const slotsDayUseRoomInfoSelector = createSelector(
  slotsDayUseDataSelector,
  slotsDayUseData => slotsDayUseData?.rooms?.[0],
);

export const dayUseSlotPlansSelector = createSelector(
  slotsDayUseDataSelector,
  slotsDayUseData => slotsDayUseData?.slotPlans,
);

export const multiRestaurantActiveAccordionDataSelector = createSelector(
  detailsUnifSelector,
  detailsData => detailsData?.multiRestaurantActiveAccordion,
);

/* ------- PropertyLayout Selectors ------- */
export const istaticDataPickSelector = createSelector(searchRoomDataSelector, searchRoomData => {
  const staticDataPick =
    !isValidArray(searchRoomData?.exactRooms) && !isValidArray(searchRoomData?.recommendedCombos);
  return staticDataPick;
});

export const privatePropertySelector = createSelector(
  [istaticDataPickSelector, hotelDetailsSelector, searchRoomDataSelector],
  (staticDataPick, hotelDetails, searchRoom) => {
    if (staticDataPick) {
      return hotelDetails?.giPrivateSpacesV2 || [];
    } else {
      return searchRoom?.exactRooms || searchRoom?.occupancyRooms || [];
    }
  },
);

export const sharedPropertySelector = createSelector(
  [istaticDataPickSelector, hotelDetailsSelector, searchRoomDataSelector],
  (staticDataPick, hotelDetails, searchRoom) => {
    if (staticDataPick) {
      return hotelDetails?.giSharedSpacesV2?.[0]?.spaces || [];
    } else {
      return searchRoom?.sharedSpaces?.spaces || [];
    }
  },
);
